import {
  CheckCircleIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useRef, useState } from "react";
import Select, { components } from "react-select";

// Custom Option component with check circle for selected items
export const SelectOptionWithCheck = (props: any) => {
  const { isSelected, children } = props;
  return (
    <components.Option {...props}>
      <div className="flex items-center justify-between w-full">
        <span>{children}</span>
        {isSelected && (
          <CheckCircleIcon className="w-4 h-4 text-orange-500 flex-shrink-0 ml-2" />
        )}
      </div>
    </components.Option>
  );
};

// Custom Menu Component with Search
export const DarkThemeMenuWithSearch = ({
  selectProps,
  ...props
}: any): JSX.Element => {
  const { onInputChange, inputValue, onMenuInputFocus } = selectProps;

  return (
    <div data-testid="custom-menu" className="flex flex-col">
      {/* Search Input */}
      <div
        className="p-2"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div className="p-2">
          <div className="flex items-center rounded-md bg-[#0b0b0b] shadow-sm">
            <div className="flex items-center px-3 py-2 w-full gap-2">
              <MagnifyingGlassIcon className="w-5 h-5 text-white stroke-2 flex-shrink-0" />
              <input
                autoCorrect="off"
                autoComplete="off"
                spellCheck="false"
                type="text"
                name="custom-search-input"
                id="custom-search-input"
                value={inputValue}
                onChange={(e) =>
                  onInputChange(e.currentTarget.value, {
                    action: "input-change",
                  })
                }
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.currentTarget.focus();
                }}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  e.currentTarget.focus();
                }}
                onFocus={onMenuInputFocus}
                placeholder="Search"
                className="font-['Inter'] flex-1 bg-transparent text-white placeholder-white border-none outline-none font-medium text-sm min-w-0"
              />
              {inputValue && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onInputChange("", { action: "input-change" });
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="flex-shrink-0 p-0.5 rounded text-gray-400 hover:text-white hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-0 focus:ring-offset-transparent"
                  aria-label="Clear search"
                  data-testid="menu-search-clear"
                >
                  <XMarkIcon className="w-5 h-5 stroke-2" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Options List */}
        <components.MenuList {...props} selectProps={selectProps} />
      </div>
    </div>
  );
};

// Custom Dropdown Indicator
export const DarkThemeDropdownIndicator = (props: any): JSX.Element => {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDownIcon className="w-5 h-5 text-white stroke-2" />
    </components.DropdownIndicator>
  );
};

// Dark Theme Group Label Formatter
export const DarkThemeGroupLabelFormatter = (data: any) => (
  <div className="font-['Inter'] text-gray-400 text-xs font-medium">
    {data.label}
  </div>
);

// Generic Dark Theme Single Select Component
interface DarkThemeSingleSelectProps {
  value: string;
  onChange: (value: string) => void;
  options:
    | Array<{ value: string; label: string }>
    | Array<{
        label: string;
        options: Array<{ value: string; label: string }>;
      }>;
  placeholder?: string;
  withSearch?: boolean;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  styles?: any;
  className?: string;
  classNamePrefix?: string;
  isDisabled?: boolean;
  customComponents?: Record<string, React.ComponentType<any>>;
  onMenuOpen?: () => void;
}

export const DarkThemeSingleSelect: React.FC<DarkThemeSingleSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select an option",
  withSearch = false,
  inputValue,
  onInputChange,
  styles: customStyles,
  className = "w-full",
  classNamePrefix,
  isDisabled = false,
  customComponents = {},
  onMenuOpen: onMenuOpenProp,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const selectContainerRef = useRef<HTMLDivElement>(null);

  // Close when focus leaves the control or menu
  useEffect(() => {
    if (!withSearch || !isFocused || !classNamePrefix) return;
    const menuSelector = `[class*="${classNamePrefix}__menu"]`;
    const handleFocusOut = (e: FocusEvent) => {
      const fromControl = selectContainerRef.current?.contains(
        e.target as Node,
      );
      const fromMenu = (e.target as HTMLElement)?.closest?.(menuSelector);
      if (!fromControl && !fromMenu) return;
      const relatedTarget = e.relatedTarget as Node | null;
      const toInsideControl =
        relatedTarget != null &&
        (selectContainerRef.current?.contains(relatedTarget) ?? false);
      const toInsideMenu =
        relatedTarget != null &&
        (relatedTarget as HTMLElement)?.closest?.(menuSelector);
      if (!toInsideControl && !toInsideMenu) {
        setIsFocused(false);
      }
    };
    document.addEventListener("focusout", handleFocusOut, true);
    return () => document.removeEventListener("focusout", handleFocusOut, true);
  }, [withSearch, isFocused, classNamePrefix]);

  // Close on Escape
  useEffect(() => {
    if (!withSearch || !isFocused) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFocused(false);
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [withSearch, isFocused]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!withSearch || !isFocused || !classNamePrefix) return;
    const handleDocumentMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideControl =
        selectContainerRef.current?.contains(target) ?? false;
      const insideMenu = (e.target as HTMLElement).closest?.(
        `[class*="${classNamePrefix}__menu"]`,
      );
      if (!insideControl && !insideMenu) {
        setIsFocused(false);
      }
    };
    document.addEventListener("mousedown", handleDocumentMouseDown, true);
    return () =>
      document.removeEventListener("mousedown", handleDocumentMouseDown, true);
  }, [withSearch, isFocused, classNamePrefix]);

  // Handle both flat and grouped options
  const findSelectedOption = (optionsList: any, targetValue: string): any => {
    for (const option of optionsList) {
      if (option.options) {
        // This is a group
        const found = option.options.find(
          (opt: any) => opt.value === targetValue,
        );
        if (found) return found;
      } else {
        // This is a flat option
        if (option.value === targetValue) return option;
      }
    }
    return null;
  };

  const selectedOption = findSelectedOption(options, value);

  const menuComponents: any = {
    DropdownIndicator: DarkThemeDropdownIndicator,
    IndicatorSeparator: () => null,
    ...customComponents,
  };

  if (withSearch) {
    menuComponents.MenuList = DarkThemeMenuWithSearch;
  }

  const selectElement = (
    <Select
      options={options}
      value={selectedOption}
      onChange={(option) => {
        onChange(option?.value || "");
        if (withSearch) {
          setIsFocused(false);
        }
      }}
      styles={customStyles || darkThemeSelectStyles}
      components={menuComponents}
      formatGroupLabel={DarkThemeGroupLabelFormatter}
      placeholder={placeholder}
      isSearchable={false}
      inputValue={inputValue}
      onInputChange={(val) => {
        if (onInputChange) {
          onInputChange(val);
        }
      }}
      onMenuOpen={() => {
        if (withSearch) setIsFocused(true);
        onMenuOpenProp?.();
      }}
      {...(withSearch && {
        menuIsOpen: isFocused || undefined,
        isFocused: isFocused || undefined,
      })}
      className={className}
      classNamePrefix={classNamePrefix}
      isDisabled={isDisabled}
    />
  );

  if (withSearch) {
    return <div ref={selectContainerRef}>{selectElement}</div>;
  }
  return selectElement;
};

// Custom styles for dark theme React Select
export const darkThemeSelectStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isDisabled ? "#2A2A2C" : "#1D1D1F",
    borderColor: "transparent",
    color: state.isDisabled ? "#7E7E81" : "#f7f7f9",
    borderRadius: "6px",
    boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.05)",
    fontFamily: "Inter, sans-serif",
    fontSize: "16px",
    fontWeight: "500",
    height: "42px",
    padding: "0",
    opacity: state.isDisabled ? 0.6 : 1,
    cursor: state.isDisabled ? "not-allowed" : "default",
    "&:hover": {
      borderColor: "transparent",
    },
    "&:focus": {
      borderColor: "transparent",
      boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.05)",
    },
  }),
  menu: (provided: any) => ({
    ...provided,
    backgroundColor: "#1D1D1F",
    border: "none",
    borderRadius: "6px",
    marginTop: "8px",
    boxShadow:
      "0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -2px rgba(0,0,0,0.05), 0px 0px 0px 2px rgba(0,0,0,0.1)",
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#2A2A2C" : "#1D1D1F",
    color: "#f7f7f9",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: "500",
    padding: "8px 16px",
    "&:hover": {
      backgroundColor: "#2A2A2C",
    },
    "&:active": {
      backgroundColor: "#2A2A2C",
    },
  }),
  multiValue: (provided: any) => ({
    ...provided,
    backgroundColor: "#424245",
    borderRadius: "4px",
  }),
  multiValueLabel: (provided: any) => ({
    ...provided,
    color: "#f7f7f9",
    fontSize: "14px",
  }),
  multiValueRemove: (provided: any) => ({
    ...provided,
    display: "none",
  }),
  noOptionsMessage: (provided: any) => ({
    ...provided,
    color: "#B4AFAF",
  }),
  placeholder: (provided: any, state: any) => ({
    ...provided,
    color: state.isDisabled ? "#7E7E81" : "#B4AFAF",
    fontFamily: "Inter, sans-serif",
    fontSize: "16px",
    fontWeight: "500",
  }),
  singleValue: (provided: any, state: any) => ({
    ...provided,
    color: state.isDisabled ? "#7E7E81" : "#f7f7f9",
    fontFamily: "Inter, sans-serif",
    fontSize: "16px",
    fontWeight: "500",
  }),
  input: (provided: any) => ({
    ...provided,
    color: "#f7f7f9",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: "500",
  }),
  menuList: (provided: any) => ({
    ...provided,
    padding: 0,
  }),
  clearIndicator: (provided: any) => ({
    ...provided,
    display: "none",
  }),
};
